import { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Users, ShoppingCart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { adminApi } from '@/api';
import { cn } from '@/lib/utils';
import { usePlatformPermission } from '@/hooks/use-platform-permission';
import { useUIStore } from '@/store/useUIStore';

const EMPTY_STATS = {
  totalRevenue: '¥0',
  revenueGrowth: '+0%',
  totalOrders: 0,
  ordersGrowth: '+0%',
  activeTenants: 0,
  tenantsGrowth: '+0%',
};

export function DashboardPage() {
  const canViewDashboard = usePlatformPermission('platform:dashboard:view');
  const isLight = useUIStore((s) => s.theme === 'light');
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);

  const loadDashboardData = useCallback(async () => {
    if (!canViewDashboard) {
      setStats(EMPTY_STATS);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const statsData = await adminApi.getAdminStats();

      const totalRevenueYuan = (statsData.overview.totalRevenue || 0) / 100;

      setStats({
        totalRevenue: `¥${totalRevenueYuan.toFixed(2)}`,
        revenueGrowth: '+12.5%',
        totalOrders: statsData.overview.orderCount,
        ordersGrowth: '+8.2%',
        activeTenants: statsData.overview.tenantCount,
        tenantsGrowth: '+3.1%',
      });
    } catch (error) {
      console.error('加载仪表板数据失败:', error);
    } finally {
      setLoading(false);
    }
  }, [canViewDashboard]);

  useEffect(() => {
    void loadDashboardData();
  }, [loadDashboardData]);

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="space-y-2">
          <div
            className={cn(
              'h-8 w-48 animate-pulse rounded-lg',
              isLight ? 'bg-slate-300/70' : 'bg-zinc-800/80',
            )}
          />
          <div
            className={cn(
              'h-4 w-72 max-w-full animate-pulse rounded-md',
              isLight ? 'bg-slate-300/60' : 'bg-zinc-800/60',
            )}
          />
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-32 rounded-2xl border border-cyan-500/10 bg-zinc-900/40 animate-pulse motion-reduce:animate-none"
              style={{ animationDelay: `${i * 80}ms` }}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {!canViewDashboard && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          当前角色无数据概览接口权限（
          <code className="font-mono text-amber-200/90">platform:dashboard:view</code>
          ），下方指标为零。请联系管理员调整平台角色权限。
        </div>
      )}
      <header
        className="space-y-1 opacity-0 motion-reduce:animate-none motion-reduce:opacity-100 animate-login-fade-up"
        style={{ animationDelay: '40ms' }}
      >
        <h1
          className={cn(
            "font-['Syne',sans-serif] text-2xl font-bold tracking-tight sm:text-3xl",
            isLight ? 'text-slate-900' : 'text-zinc-50',
          )}
        >
          数据概览
        </h1>
        <p className={cn('text-sm', isLight ? 'text-slate-500' : 'text-zinc-500')}>
          查看系统运营数据和关键指标
        </p>
      </header>

      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          delay={80}
          iconWrapClass="border-cyan-400/25 bg-cyan-500/10 text-cyan-300 shadow-[0_0_24px_-8px_rgba(34,211,238,0.45)]"
          icon={<TrendingUp className="h-5 w-5" />}
          title="总收入"
          value={stats.totalRevenue}
          trend={stats.revenueGrowth}
          trendType="up"
          isLight={isLight}
        />
        <KpiCard
          delay={140}
          iconWrapClass="border-emerald-400/25 bg-emerald-500/10 text-emerald-300 shadow-[0_0_24px_-8px_rgba(52,211,153,0.35)]"
          icon={<ShoppingCart className="h-5 w-5" />}
          title="总订单数"
          value={stats.totalOrders.toString()}
          trend={stats.ordersGrowth}
          trendType="up"
          isLight={isLight}
        />
        <KpiCard
          delay={200}
          iconWrapClass="border-fuchsia-400/25 bg-fuchsia-500/10 text-fuchsia-300 shadow-[0_0_24px_-8px_rgba(217,70,239,0.35)]"
          icon={<Users className="h-5 w-5" />}
          title="活跃租户"
          value={stats.activeTenants.toString()}
          trend={stats.tenantsGrowth}
          trendType="up"
          isLight={isLight}
        />
      </div>

    </div>
  );
}

function KpiCard({
  icon,
  title,
  value,
  trend,
  trendType,
  iconWrapClass,
  delay,
  isLight,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  trend: string;
  trendType: 'up' | 'down';
  iconWrapClass: string;
  delay: number;
  isLight: boolean;
}) {
  return (
    <Card
      className={cn(
        isLight
          ? 'group relative overflow-hidden border-slate-300/85 bg-white/85 shadow-[0_8px_30px_-22px_rgba(15,23,42,0.22)]'
          : 'group relative overflow-hidden border-cyan-500/12 bg-zinc-900/40 backdrop-blur-md shadow-[0_0_0_1px_rgba(34,211,238,0.05)]',
        'transition-all duration-300 motion-reduce:transition-none',
        isLight
          ? 'hover:border-cyan-400/35 hover:shadow-[0_10px_32px_-20px_rgba(8,145,178,0.28)]'
          : 'hover:border-cyan-400/28 hover:shadow-[0_0_48px_-16px_rgba(34,211,238,0.28)]',
        'opacity-0 motion-reduce:animate-none motion-reduce:opacity-100 animate-login-fade-up',
      )}
      style={{ animationDelay: `${delay}ms` }}
    >
      {!isLight && (
        <span
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          aria-hidden
        />
      )}
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-3">
          <div
            className={cn(
              'flex h-11 w-11 items-center justify-center rounded-xl border',
              iconWrapClass,
            )}
          >
            {icon}
          </div>
          <Badge
            variant={trendType === 'up' ? 'default' : 'destructive'}
            className={cn(
              'shrink-0 border-0 shadow-sm',
              trendType === 'up' &&
                (isLight
                  ? 'bg-cyan-100 text-cyan-700 hover:bg-cyan-100'
                  : 'bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/25'),
              trendType === 'down' &&
                (isLight
                  ? 'bg-rose-100 text-rose-700 hover:bg-rose-100'
                  : 'bg-red-500/20 text-red-200 hover:bg-red-500/25'),
            )}
          >
            {trend}
          </Badge>
        </div>
        <div className="mt-5">
          <p className={cn('text-sm', isLight ? 'text-slate-500' : 'text-zinc-500')}>{title}</p>
          <p className={cn("mt-1 font-['Syne',sans-serif] text-2xl font-bold tracking-tight", isLight ? 'text-slate-900' : 'text-zinc-50')}>
            {value}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

